import { useState, useRef, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import AnimatedSection from '../../components/base/AnimatedSection';
import { projects } from '../../mocks/projects';

const PortfolioPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const categories = [
    { label: '전체', value: '전체' },
    { label: '주거', value: '주거' },
    { label: '비주거', value: '비주거' },
  ];

  // 주거 카테고리에 포함되는 타입들
  const residentialTypes = ['민간임대', '지역주택조합', '주거'];
  // 비주거 카테고리에 포함되는 타입들
  const nonResidentialTypes = ['지식산업센터', '오피스텔'];

  const filteredProjects =
    selectedCategory === '전체'
      ? projects
      : selectedCategory === '주거'
      ? projects.filter((p) => residentialTypes.includes(p.type))
      : projects.filter((p) => nonResidentialTypes.includes(p.type));

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: '포트폴리오 - GB더플래닛',
    description: 'APT, 오피스텔, 상가, 오피스 등 GB더플래닛의 주요 프로젝트 실적을 소개합니다',
    url: typeof window !== 'undefined' ? `${window.location.origin}/portfolio` : '',
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: projects.slice(0, 10).map((project, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'RealEstateProject',
          name: project.name,
          description: `${project.location} - ${project.scale}`,
          location: {
            '@type': 'Place',
            address: project.location,
          },
        },
      })),
    },
  };

  return (
    <div className="min-h-screen bg-white">
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <Navbar />

      <div className="pt-20">
        {/* Filter Section */}
        <section className="bg-white py-3 md:py-5 border-b border-gray-200">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-navy font-english">Portfolio</h1>
              <p className="text-xs md:text-sm text-gray-500 mt-1">APT / 오피스텔 / 상가 / 오피스 등 GB더플래닝 주요 프로젝트 실적 내역을 소개합니다.</p>
            </div>
            
            {/* 드롭다운 필터 */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-navy hover:border-gold transition-all cursor-pointer min-w-[140px] justify-between"
              >
                <span>{selectedCategory}</span>
                <i className={`ri-arrow-down-s-line transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>
              
              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden z-50">
                  {categories.map((category) => (
                    <button
                      key={category.value}
                      onClick={() => {
                        setSelectedCategory(category.value);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-2.5 text-left text-sm transition-all cursor-pointer ${
                        selectedCategory === category.value
                          ? 'bg-gold text-white font-medium'
                          : 'text-navy hover:bg-gray-50'
                      }`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="bg-gray-50 py-10 md:py-16">
          <div className="max-w-[1200px] mx-auto px-4 md:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3 md:gap-4 lg:gap-6">
              {filteredProjects.map((project, index) => (
                <AnimatedSection key={project.id} animation="fadeUp" delay={(index % 3) * 100} className="h-full">
                  <div className="bg-white rounded-xl md:rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full flex flex-col">
                    <div className="relative h-32 sm:h-40 md:h-48 lg:h-64 overflow-hidden flex-shrink-0">
                      <img
                        src={project.image}
                        alt={project.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-3 md:p-4 lg:p-5 flex-1 flex flex-col">
                      <h3 className="text-sm md:text-base lg:text-lg font-bold text-navy mb-2 md:mb-2.5 lg:mb-3 line-clamp-2 break-words leading-snug min-h-[2.5rem] md:min-h-[3rem]">{project.name}</h3>
                      <div className="space-y-1 md:space-y-1.5 text-[11px] md:text-xs text-gray-600 mt-auto">
                        <p className="flex items-start gap-1.5 md:gap-2">
                          <i className="ri-map-pin-line text-gold flex-shrink-0 mt-0.5 text-xs md:text-sm"></i>
                          <span className="break-words line-clamp-2 leading-relaxed">{project.location}</span>
                        </p>
                        <p className="flex items-start gap-1.5 md:gap-2">
                          <i className="ri-building-line text-gold flex-shrink-0 mt-0.5 text-xs md:text-sm"></i>
                          <span className="break-words line-clamp-1">{project.scale}</span>
                        </p>
                        <p className="flex items-start gap-1.5 md:gap-2">
                          <i className="ri-calendar-line text-gold flex-shrink-0 mt-0.5 text-xs md:text-sm"></i>
                          <span className="break-words">{project.completion}</span>
                        </p>
                        <p className="flex items-start gap-1.5 md:gap-2 text-gold font-medium">
                          <i className="ri-hammer-line flex-shrink-0 mt-0.5 text-xs md:text-sm"></i>
                          <span className="break-words line-clamp-1">{project.constructor}</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>

            {filteredProjects.length === 0 && (
              <AnimatedSection animation="fadeIn">
                <div className="text-center py-12 md:py-16">
                  <i className="ri-folder-open-line text-4xl md:text-5xl text-gray-300 mb-3"></i>
                  <p className="text-gray-500 text-sm md:text-base">해당 유형의 프로젝트가 없습니다.</p>
                </div>
              </AnimatedSection>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default PortfolioPage;
